import { API } from '../constants/constants';
import { Engagement, EngagementRequestPayload, UpdateEngagementWithFilesMutationProps } from '../types';
import { normalizeToIds, normalizeToSubs } from '../../utils/eventHandlerUtils';

export const buildEngagementCreatePayload = (payload: Engagement): EngagementRequestPayload => {
  const communityMembersSource =
    (payload as Engagement & { communityMembers?: unknown }).communityMembers ?? payload.communityMember;
  const organizationsSource =
    (payload as Engagement & { organizations?: unknown }).organizations ?? payload.organization;
  const {
    year: _year,
    month: _month,
    ...rest
  } = payload as Engagement & { year?: string; month?: string };
  const requestPayload = {
    ...rest,
    communityMembers: normalizeToIds(
      communityMembersSource as string | { _id?: string; id?: string } | Array<string | { _id?: string; id?: string }>
    ),
    organizations: normalizeToIds(
      organizationsSource as string | { _id?: string; id?: string } | Array<string | { _id?: string; id?: string }>
    ),
    lead: normalizeToIds(payload.lead),
  };
  delete requestPayload.communityMember;
  delete requestPayload.organization;
  return requestPayload;
};

export const buildEngagementUpdatePayload = (payload: Engagement): EngagementRequestPayload => {
  const communityMembersSource =
    (payload as Engagement & { communityMembers?: unknown }).communityMembers ?? payload.communityMember;
  const organizationsSource =
    (payload as Engagement & { organizations?: unknown }).organizations ?? payload.organization;
  const {
    year: _year,
    month: _month,
    ...rest
  } = payload as Engagement & { year?: string; month?: string };
  const requestPayload = {
    ...rest,
    communityMembers: normalizeToIds(
      communityMembersSource as string | { _id?: string; id?: string } | Array<string | { _id?: string; id?: string }>
    ),
    organizations: normalizeToIds(
      organizationsSource as string | { _id?: string; id?: string } | Array<string | { _id?: string; id?: string }>
    ),
    lead: normalizeToSubs(payload.lead),
  };
  delete requestPayload.communityMember;
  delete requestPayload.organization;
  return requestPayload;
};

export const getMonthlyEngagements = async (month: string, timezoneOffset: string) => {
  const res = await API.get('/engagements/monthly', {
    params: {
      month,
      timezoneOffset,
    },
  });
  return res.data;
};

export const createEngagement = async (payload: Engagement | EngagementRequestPayload) => {
  const normalizedPayload = buildEngagementCreatePayload(payload as Engagement);
  const res = await API.post(`/engagement`, normalizedPayload);
  return res;
};

interface UploadEngagementFilesURLProps {
  id: string;
  files: File[];
}

export const uploadEngagementFiles = async ({ id, files }: UploadEngagementFilesURLProps) => {
  const formData = new FormData();
  files.filter((file) => file.type !== 'url').forEach((file) => formData.append('file', file));

  return API.post(`/engagement/${id}/upload`, formData, {
    headers: { 'content-type': 'multipart/form-data' },
  });
};

interface UploadEngagementURLProps {
  id: string;
  file: File;
}

export const uploadEngagementUrl = async ({ id, file }: UploadEngagementURLProps) => {
  return API.post(`/engagement/${id}/url`, file);
};

export const updateEngagementWithFiles = async ({
  id,
  fields,
  uploadingFiles,
  uploadingUrls,
  removingFiles,
  removingUrls,
}: UpdateEngagementWithFilesMutationProps) => {
  const formData = new FormData();
  for (const file of uploadingFiles) {
    formData.append('file', file);
  }
  formData.append('urls', JSON.stringify(uploadingUrls));
  formData.append('deletingFiles', JSON.stringify(removingFiles));
  formData.append('deletingUrls', JSON.stringify(removingUrls));
  const normalizedFields = buildEngagementUpdatePayload(fields);
  formData.append('fields', JSON.stringify(normalizedFields));

  const res = await API.put(`/engagements/${id}/update`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
};

export const deleteEngagement = async (id: string) => {
  const res = await API.delete(`/engagement/${id}`);
  return res.data;
};

export const getFilteredEngagements = async (filterQueryString: string) => {
  const res = await API.get(`engagements/filter?${filterQueryString}`);
  return res.data?.data;
};

export const getFuzzySearchEngagements = async (queryString: string, category: string) => {
  const res = await API.get(`/engagements/fuzzySearch?queryString=${queryString}&category=${category}`);
  return res.data?.data;
};
